'use client'

import React from 'react'
import { 
  Clock, 
  CheckCircle, 
  XCircle, 
  AlertCircle,
  FileText,
  Mail,
  User,
  TrendingUp,
  CircleCheckBig,
  CircleX,
  CircleAlert,
  UserCheck,
  FileCheck,
  Send,
  Users,
  Pause,
  Play,
  Target,
  Ban,
  DollarSign,
  Timer,
  Archive,
  Crown,
  RefreshCw
} from 'lucide-react'

interface UntitledBadgeProps {
  status?: string | undefined | null
  size?: 'sm' | 'md' | 'lg'
  showIcon?: boolean
  className?: string
  variant?: 'modern' | 'modern-inset' | 'outline'
  timestamp?: string
  showTimestamp?: boolean
  children?: React.ReactNode
}

type BadgeConfig = {
  color: {
    modern: string
    modernInset: string
    outline: string
  }
  icon: React.ReactNode
  label: string
}

export function UntitledBadge({ 
  status, 
  size = 'md',
  showIcon = true,
  className = '',
  variant = 'modern',
  timestamp,
  showTimestamp = false,
  children
}: UntitledBadgeProps) {
  
  const getStatusConfig = (status: string | undefined | null): BadgeConfig => {
    switch (status) {
      // Quote/Approval statuses
      case 'pending_approval':
        return {
          color: {
            modern: 'bg-warning-50 text-warning-700 border border-warning-200',
            modernInset: 'bg-warning-50 text-warning-700 ring-1 ring-inset ring-warning-600/20',
            outline: 'border border-warning-300 text-warning-700'
          },
          icon: <Clock className="h-3 w-3" />,
          label: 'Pending Approval'
        }
      case 'approved':
        return {
          color: {
            modern: 'bg-blue-50 text-blue-700 border border-blue-200',
            modernInset: 'bg-blue-50 text-blue-700 ring-1 ring-inset ring-blue-600/20',
            outline: 'border border-blue-300 text-blue-700'
          },
          icon: <RefreshCw className="h-3 w-3 animate-spin" />,
          label: 'Sending to Client...'
        }
      case 'skipped_client_send':
        return {
          color: {
            modern: 'bg-green-50 text-green-700 border border-green-200',
            modernInset: 'bg-green-50 text-green-700 ring-1 ring-inset ring-green-600/20',
            outline: 'border border-green-300 text-green-700'
          },
          icon: <CheckCircle className="h-3 w-3" />,
          label: 'Approved (Not Sent)'
        }
      case 'sent_to_client':
        return {
          color: {
            modern: 'bg-primary-50 text-primary-700 border border-primary-200',
            modernInset: 'bg-primary-50 text-primary-700 ring-1 ring-inset ring-primary-600/20',
            outline: 'border border-primary-300 text-primary-700'
          },
          icon: <Send className="h-3 w-3" />,
          label: 'Sent to Client'
        }
      case 'accepted':
        return {
          color: {
            modern: 'bg-green-500 text-white border-2 border-green-300 animate-pulse-slow',
            modernInset: 'bg-green-500 text-white ring-2 ring-green-300 animate-pulse-slow',
            outline: 'border-2 border-green-500 text-green-700 bg-green-50 animate-pulse-slow'
          },
          icon: <CircleCheckBig className="h-3 w-3" />,
          label: 'Accepted - Ready to Onboard'
        }
      case 'awaiting_intake':
        return {
          color: {
            modern: 'bg-yellow-500 text-white border-2 border-yellow-300',
            modernInset: 'bg-yellow-500 text-white ring-2 ring-yellow-300',
            outline: 'border-2 border-yellow-500 text-yellow-700 bg-yellow-50'
          },
          icon: <Clock className="h-3 w-3" />,
          label: 'Accepted - Awaiting Intake'
        }
      case 'rejected':
        return {
          color: {
            modern: 'bg-error-50 text-error-700 border border-error-200',
            modernInset: 'bg-error-50 text-error-700 ring-1 ring-inset ring-error-600/20',
            outline: 'border border-error-300 text-error-700'
          },
          icon: <CircleX className="h-3 w-3" />,
          label: 'Rejected'
        }
      case 'error':
        return {
          color: {
            modern: 'bg-error-50 text-error-700 border border-error-200',
            modernInset: 'bg-error-50 text-error-700 ring-1 ring-inset ring-error-600/20',
            outline: 'border border-error-300 text-error-700'
          },
          icon: <CircleAlert className="h-3 w-3" />,
          label: 'Error'
        }
      case 'edited':
        return {
          color: {
            modern: 'bg-purple-50 text-purple-700 border border-purple-200',
            modernInset: 'bg-purple-50 text-purple-700 ring-1 ring-inset ring-purple-600/20',
            outline: 'border border-purple-300 text-purple-700'
          },
          icon: <FileCheck className="h-3 w-3" />,
          label: 'Edited'
        }
      case 'converted':
        return {
          color: {
            modern: 'bg-emerald-50 text-emerald-700 border border-emerald-200',
            modernInset: 'bg-emerald-50 text-emerald-700 ring-1 ring-inset ring-emerald-600/20',
            outline: 'border border-emerald-300 text-emerald-700'
          },
          icon: <CheckCircle className="h-3 w-3" />,
          label: 'Converted'
        }
      
      // Lifecycle stages
      case 'prospect':
        return {
          color: {
            modern: 'bg-warning-50 text-warning-700 border border-warning-200',
            modernInset: 'bg-warning-50 text-warning-700 ring-1 ring-inset ring-warning-600/20',
            outline: 'border border-warning-300 text-warning-700'
          },
          icon: <User className="h-3 w-3" />,
          label: 'Prospect'
        }
      case 'lead':
        return {
          color: {
            modern: 'bg-orange-50 text-orange-700 border border-orange-200',
            modernInset: 'bg-orange-50 text-orange-700 ring-1 ring-inset ring-orange-600/20',
            outline: 'border border-orange-300 text-orange-700'
          },
          icon: <Target className="h-3 w-3" />,
          label: 'Lead'
        }
      case 'opportunity':
        return {
          color: {
            modern: 'bg-warning-50 text-warning-700 border border-warning-200',
            modernInset: 'bg-warning-50 text-warning-700 ring-1 ring-inset ring-warning-600/20',
            outline: 'border border-warning-300 text-warning-700'
          },
          icon: <TrendingUp className="h-3 w-3" />,
          label: 'Opportunity'
        }
      
      // Contact statuses
      case 'engaged':
        return {
          color: {
            modern: 'bg-success-50 text-success-700 border border-success-200',
            modernInset: 'bg-success-50 text-success-700 ring-1 ring-inset ring-success-600/20',
            outline: 'border border-success-300 text-success-700'
          },
          icon: <UserCheck className="h-3 w-3" />,
          label: 'Engaged'
        }
      case 'champion':
        return {
          color: {
            modern: 'bg-primary-50 text-primary-700 border border-primary-200',
            modernInset: 'bg-primary-50 text-primary-700 ring-1 ring-inset ring-primary-600/20',
            outline: 'border border-primary-300 text-primary-700'
          },
          icon: <Crown className="h-3 w-3" />,
          label: 'Champion'
        }
      case 'unresponsive':
        return {
          color: {
            modern: 'bg-error-50 text-error-700 border border-error-200',
            modernInset: 'bg-error-50 text-error-700 ring-1 ring-inset ring-error-600/20',
            outline: 'border border-error-300 text-error-700'
          },
          icon: <Ban className="h-3 w-3" />,
          label: 'Unresponsive'
        }
      case 'client':
        return {
          color: {
            modern: 'bg-success-50 text-success-700 border border-success-200',
            modernInset: 'bg-success-50 text-success-700 ring-1 ring-inset ring-success-600/20',
            outline: 'border border-success-300 text-success-700'
          },
          icon: <UserCheck className="h-3 w-3" />,
          label: 'Client'
        }
      case 'onboarding':
      case 'client_onboarding':
        return {
          color: {
            modern: 'bg-primary-50 text-primary-700 border border-primary-200',
            modernInset: 'bg-primary-50 text-primary-700 ring-1 ring-inset ring-primary-600/20',
            outline: 'border border-primary-300 text-primary-700'
          },
          icon: <Users className="h-3 w-3" />,
          label: 'Onboarding'
        }
      case 'partner':
        return {
          color: {
            modern: 'bg-indigo-50 text-indigo-700 border border-indigo-200',
            modernInset: 'bg-indigo-50 text-indigo-700 ring-1 ring-inset ring-indigo-600/20',
            outline: 'border border-indigo-300 text-indigo-700'
          },
          icon: <Users className="h-3 w-3" />,
          label: 'Partner'
        }
      
      // Company statuses
      case 'active':
        return {
          color: {
            modern: 'bg-success-50 text-success-700 border border-success-200',
            modernInset: 'bg-success-50 text-success-700 ring-1 ring-inset ring-success-600/20',
            outline: 'border border-success-300 text-success-700'
          },
          icon: <Play className="h-3 w-3" />,
          label: 'Active'
        }
      case 'inactive':
        return {
          color: {
            modern: 'bg-gray-50 text-gray-700 border border-gray-200',
            modernInset: 'bg-gray-50 text-gray-700 ring-1 ring-inset ring-gray-600/20',
            outline: 'border border-gray-300 text-gray-700'
          },
          icon: <Archive className="h-3 w-3" />,
          label: 'Inactive'
        }
      case 'pipeline':
        return {
          color: {
            modern: 'bg-primary-50 text-primary-700 border border-primary-200',
            modernInset: 'bg-primary-50 text-primary-700 ring-1 ring-inset ring-primary-600/20',
            outline: 'border border-primary-300 text-primary-700'
          },
          icon: <TrendingUp className="h-3 w-3" />,
          label: 'Pipeline'
        }
      case 'churned':
        return {
          color: {
            modern: 'bg-error-50 text-error-700 border border-error-200',
            modernInset: 'bg-error-50 text-error-700 ring-1 ring-inset ring-error-600/20',
            outline: 'border border-error-300 text-error-700'
          },
          icon: <Ban className="h-3 w-3" />,
          label: 'Churned'
        }
      case 'paused':
        return {
          color: {
            modern: 'bg-gray-50 text-gray-700 border border-gray-200',
            modernInset: 'bg-gray-50 text-gray-700 ring-1 ring-inset ring-gray-600/20',
            outline: 'border border-gray-300 text-gray-700'
          },
          icon: <Pause className="h-3 w-3" />,
          label: 'Paused'
        }
      case 'pending_onboarding':
        return {
          color: {
            modern: 'bg-warning-50 text-warning-700 border border-warning-200',
            modernInset: 'bg-warning-50 text-warning-700 ring-1 ring-inset ring-warning-600/20',
            outline: 'border border-warning-300 text-warning-700'
          },
          icon: <Clock className="h-3 w-3" />,
          label: 'Pending Onboarding'
        }
      
      // Invoice/Payment statuses
      case 'draft':
        return {
          color: {
            modern: 'bg-gray-50 text-gray-700 border border-gray-200',
            modernInset: 'bg-gray-50 text-gray-700 ring-1 ring-inset ring-gray-600/20',
            outline: 'border border-gray-300 text-gray-700'
          },
          icon: <FileText className="h-3 w-3" />,
          label: 'Draft'
        }
      case 'sent':
        return {
          color: {
            modern: 'bg-primary-50 text-primary-700 border border-primary-200',
            modernInset: 'bg-primary-50 text-primary-700 ring-1 ring-inset ring-primary-600/20',
            outline: 'border border-primary-300 text-primary-700'
          },
          icon: <Send className="h-3 w-3" />,
          label: 'Sent'
        }
      case 'paid':
        return {
          color: {
            modern: 'bg-success-50 text-success-700 border border-success-200',
            modernInset: 'bg-success-50 text-success-700 ring-1 ring-inset ring-success-600/20',
            outline: 'border border-success-300 text-success-700'
          },
          icon: <DollarSign className="h-3 w-3" />,
          label: 'Paid'
        }
      case 'partial':
        return {
          color: {
            modern: 'bg-warning-50 text-warning-700 border border-warning-200',
            modernInset: 'bg-warning-50 text-warning-700 ring-1 ring-inset ring-warning-600/20',
            outline: 'border border-warning-300 text-warning-700'
          },
          icon: <Timer className="h-3 w-3" />,
          label: 'Partial'
        }
      case 'overdue':
        return {
          color: {
            modern: 'bg-error-50 text-error-700 border border-error-200',
            modernInset: 'bg-error-50 text-error-700 ring-1 ring-inset ring-error-600/20',
            outline: 'border border-error-300 text-error-700'
          },
          icon: <CircleAlert className="h-3 w-3" />,
          label: 'Overdue'
        }
      case 'cancelled':
        return {
          color: {
            modern: 'bg-gray-50 text-gray-700 border border-gray-200',
            modernInset: 'bg-gray-50 text-gray-700 ring-1 ring-inset ring-gray-600/20',
            outline: 'border border-gray-300 text-gray-700'
          },
          icon: <Ban className="h-3 w-3" />,
          label: 'Cancelled'
        }
      
      // Other statuses
      case 'completed':
        return {
          color: {
            modern: 'bg-success-50 text-success-700 border border-success-200',
            modernInset: 'bg-success-50 text-success-700 ring-1 ring-inset ring-success-600/20',
            outline: 'border border-success-300 text-success-700'
          },
          icon: <FileCheck className="h-3 w-3" />,
          label: 'Completed'
        }

      // Sync statuses
      case 'success':
        return {
          color: {
            modern: 'bg-green-50 text-green-700 border border-green-200',
            modernInset: 'bg-green-50 text-green-700 ring-1 ring-inset ring-green-600/20',
            outline: 'border border-green-300 text-green-700'
          },
          icon: <CheckCircle className="h-3 w-3" />,
          label: 'Success'
        }
      case 'warning':
        return {
          color: {
            modern: 'bg-yellow-50 text-yellow-700 border border-yellow-200',
            modernInset: 'bg-yellow-50 text-yellow-700 ring-1 ring-inset ring-yellow-600/20',
            outline: 'border border-yellow-300 text-yellow-700'
          },
          icon: <AlertCircle className="h-3 w-3" />,
          label: 'Warning'
        }
      case 'purple':
        return {
          color: {
            modern: 'bg-purple-50 text-purple-700 border border-purple-200',
            modernInset: 'bg-purple-50 text-purple-700 ring-1 ring-inset ring-purple-600/20',
            outline: 'border border-purple-300 text-purple-700'
          },
          icon: <RefreshCw className="h-3 w-3" />,
          label: 'Automatic'
        }
      case 'orange':
        return {
          color: {
            modern: 'bg-orange-50 text-orange-700 border border-orange-200',
            modernInset: 'bg-orange-50 text-orange-700 ring-1 ring-inset ring-orange-600/20',
            outline: 'border border-orange-300 text-orange-700'
          },
          icon: <DollarSign className="h-3 w-3" />,
          label: 'Price Update'
        }
      case 'blue':
        return {
          color: {
            modern: 'bg-blue-50 text-blue-700 border border-blue-200',
            modernInset: 'bg-blue-50 text-blue-700 ring-1 ring-inset ring-blue-600/20',
            outline: 'border border-blue-300 text-blue-700'
          },
          icon: <User className="h-3 w-3" />,
          label: 'Manual'
        }
      case 'gray':
        return {
          color: {
            modern: 'bg-gray-50 text-gray-700 border border-gray-200',
            modernInset: 'bg-gray-50 text-gray-700 ring-1 ring-inset ring-gray-600/20',
            outline: 'border border-gray-300 text-gray-700'
          },
          icon: <FileText className="h-3 w-3" />,
          label: 'Unknown'
        }
      default:
        return {
          color: {
            modern: 'bg-gray-50 text-gray-700 border border-gray-200',
            modernInset: 'bg-gray-50 text-gray-700 ring-1 ring-inset ring-gray-600/20',
            outline: 'border border-gray-300 text-gray-700'
          },
          icon: <FileText className="h-3 w-3" />,
          label: (status || 'unknown').replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
        }
    }
  }

  const getSizeClasses = (size: string, variant: string) => {
    const baseClasses = {
      sm: 'text-xs',
      md: 'text-sm',
      lg: 'text-sm'
    }

    const paddingClasses = {
      sm: showIcon ? 'px-2 py-0.5 gap-1' : 'px-2 py-0.5',
      md: showIcon ? 'px-2.5 py-0.5 gap-1.5' : 'px-2.5 py-0.5',
      lg: showIcon ? 'px-3 py-1 gap-1.5' : 'px-3 py-1'
    }

    return `${baseClasses[size as keyof typeof baseClasses]} ${paddingClasses[size as keyof typeof paddingClasses]}`
  }

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp)
    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60))
    const diffDays = Math.floor(diffHours / 24)
    
    if (diffDays > 0) {
      return `${diffDays}d ago`
    } else if (diffHours > 0) {
      return `${diffHours}h ago`
    } else {
      return 'Recent'
    }
  }

  // If children are provided, render as a simple badge without status
  if (children) {
    const defaultColorClass = variant === 'modern' 
      ? 'bg-gray-100 text-gray-700 border border-gray-200'
      : variant === 'modern-inset'
      ? 'bg-gray-100 text-gray-700 ring-1 ring-inset ring-gray-600/20'
      : 'border border-gray-300 text-gray-700'
    
    return (
      <div className={`inline-flex items-center justify-center rounded-md font-medium ${defaultColorClass} ${getSizeClasses(size, variant)} ${className}`}>
        {children}
      </div>
    )
  }

  const config = getStatusConfig(status)
  const colorClass = config.color[variant === 'modern' ? 'modern' : variant === 'modern-inset' ? 'modernInset' : 'outline']

  if (showTimestamp && timestamp) {
    return (
      <div className={`flex flex-col gap-1 ${className}`}>
        <div className={`inline-flex items-center justify-center rounded-md font-medium ${colorClass} ${getSizeClasses(size, variant)}`}>
          {showIcon && config.icon}
          <span>{config.label}</span>
        </div>
        <div className="text-xs text-base-content/60 ml-1">
          {formatTimestamp(timestamp)}
        </div>
      </div>
    )
  }

  return (
    <div className={`inline-flex items-center justify-center rounded-md font-medium ${colorClass} ${getSizeClasses(size, variant)} ${className}`}>
      {showIcon && config.icon}
      <span>{config.label}</span>
    </div>
  )
}

// Add default export as well
export default UntitledBadge